import { FormInputBlock } from './form-input-block.js'
import type { NameAndType } from '../../types.js'
import { jsonClass, jsonProperty } from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { Expression } from '../../data/text-or-code.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'
import { BoReference } from '../../bos/bo-reference.js'
import { Screen } from '../../bos/screen.js'

export const StaticEntityBlockOptionsSources = {
	staticEntity: 'Static Entity',
	custom: 'Custom (e.g. array of Choices or other objects)',
}
export type StaticEntityBlockOptionsSource =
	keyof typeof StaticEntityBlockOptionsSources

@jsonClass('StaticEntityBlock')
export class StaticEntityBlock extends FormInputBlock {
	isMultipleSelection: boolean = false

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 1,
		label: 'Label',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
	})
	labelCode = new TextOrExpression_assumeExpression('', 'T')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: 'Binding',
		codeLanguage: 'ts',
		tsReturnType: (blocks: StaticEntityBlock[]) =>
			`(string | number | object)${blocks[0].isMultipleSelection ? '[]' : ''}`,
		editorSize: 'singleline',
		isBinding: true,
		isMandatory: () => true,
	})
	bindingCode = new Expression()

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 3,
		label: 'Options list source',
		options: Object.entries(StaticEntityBlockOptionsSources),
	})
	optionListSource: StaticEntityBlockOptionsSource = 'custom'

	@jsonProperty(BoReference)
	@ScreenEditorInput<StaticEntityBlock>({
		inputType: 'boRef',
		order: 4,
		label: 'Options list StaticEntity',
		allowedBoTypes: ['StaticEntity'],
		includeImportedModules: true,
		isVisible: (blocks) => blocks[0]?.optionListSource == 'staticEntity',
		isMandatory: () => true,
	})
	optionListSourceStaticEntityRef = new BoReference<'StaticEntity'>()

	@jsonProperty()
	@ScreenEditorInput<StaticEntityBlock>({
		inputType: 'boRefDetail',
		order: 5,
		label: 'Display column',
		detailName: 'allPropertyNames',
		boRef: (blocks) => blocks[0].optionListSourceStaticEntityRef,
		isVisible: (blocks) => blocks[0]?.optionListSource == 'staticEntity',
		isMandatory: () => true,
	})
	optionListSourceStaticEntityDisplayProperty: string = '$displayValue'

	@jsonProperty(Expression)
	@ScreenEditorInput<StaticEntityBlock>({
		inputType: 'code',
		order: 4,
		label: 'Options list',
		codeLanguage: 'ts',
		tsReturnType: () => 'any[]',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.optionListSource == 'custom',
		isMandatory: () => true,
	})
	optionListCode = new Expression()

	@jsonProperty(Expression)
	@ScreenEditorInput<StaticEntityBlock>({
		inputType: 'code',
		order: 5,
		label: 'Option value',
		codeLanguage: 'ts',
		tsReturnType: () => 'string | number | object',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.optionListSource == 'custom',
		isMandatory: () => true,
		additionalLines: (blocks: StaticEntityBlock[]) => [
			`const $option = (${blocks[0].optionListCode.content})[0]`.replace(
				/\n/g,
				' ',
			),
		],
		additionalVariables: () => ({
			$index: 'number',
		}),
		tooltip: () => ({
			text: 'Use <code>$option</code> to access each item of the options list and <code>$index</code> to access the zero-based index of the current item',
			acceptHtml: true,
		}),
	})
	optionValueCode = new Expression('$option.id')

	@jsonProperty(Expression)
	@ScreenEditorInput<StaticEntityBlock>({
		inputType: 'code',
		order: 6,
		label: 'Option label',
		codeLanguage: 'ts',
		tsReturnType: () => 'string | number',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.optionListSource == 'custom',
		isMandatory: () => true,
		additionalLines: (blocks: StaticEntityBlock[]) => [
			`const $options = (${blocks[0].optionListCode.content})`.replace(
				/\n/g,
				' ',
			),
		],
		additionalVariables: () => ({
			$option: `typeof $options[number]`,
			$index: 'number',
		}),
		tooltip: () => ({
			text: 'Use <code>$option</code> to access each item of the options list and <code>$index</code> to access the zero-based index of the current item',
			acceptHtml: true,
		}),
	})
	optionLabelCode = new Expression('$option.label')

	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 10,
		label: 'Show filter',
	})
	@jsonProperty(Boolean)
	showFilter: boolean = false

	@ScreenEditorInput<StaticEntityBlock>({
		inputType: 'code',
		order: 11,
		label: 'Object properties to filter for',
		codeLanguage: 'ts',
		tsReturnType: () => `'*' | string[]`,
		editorSize: 'singleline',
		isBinding: false,
		tooltip: () => ({
			text: `If option list returns an object, only use the specified property names for filtering. Specify <code>'*'</code> to consider all properties. If option list is not a list ob objects (but e.g. strings), this has no effect`,
			acceptHtml: true,
		}),
		isVisible: (blocks) => blocks[0].showFilter,
	})
	@jsonProperty(Expression)
	filterPropertiesExpression: Expression = new Expression(`'*'`)

	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 12,
		label: 'Allow clearing value',
	})
	@jsonProperty(Boolean)
	allowClearing: boolean = false

	canAcceptVariableString = (variable: NameAndType) => !variable.isArray
	acceptVariableString = (variable: NameAndType, screen: Screen) => {
		this.bindingCode = new Expression(variable.name)
		if (variable.type.split('.').length == 2) {
			// in the format of a static entity qualified name
			this.optionListSource = 'staticEntity'
			this.optionListSourceStaticEntityRef = BoReference.fromQualifiedName(
				variable.type,
				'StaticEntity',
			).forModule(screen.moduleId)
		}
	}

	constructor(init: Partial<StaticEntityBlock> = {}) {
		super()
		this.init(init)
	}

	static createNewForEditor() {
		return new this({
			optionListSource: 'staticEntity',
		})
	}

	getEffectiveCodes() {
		switch (this.optionListSource) {
			case 'custom':
				return {
					listCode: this.optionListCode.getAsTemplateExpressionOrDefault('[]'),
					valueCode:
						this.optionValueCode.getAsTemplateExpressionOrDefault('$option.id'),
					labelCode:
						this.optionLabelCode.getAsTemplateExpressionOrDefault('$option.id'),
				}
			case 'staticEntity':
				return {
					listCode: `${this.optionListSourceStaticEntityRef.getQualifiedName()}.getChoices('${this.optionListSourceStaticEntityDisplayProperty}')`,
					valueCode: `${this.optionListSourceStaticEntityRef.getQualifiedName()}.getEntry($option.id)`,
					labelCode: '$option.label',
				}
			default:
				throw new Error(
					`Unsupported optionListSource: ${this.optionListSource}`,
				)
		}
	}

	getValidationValueType(): string {
		const type =
			this.optionListSource == 'staticEntity'
				? this.optionListSourceStaticEntityRef.getQualifiedName()
				: 'any'
		return this.isMultipleSelection ? `${type}[]` : type
	}
}
